import { useCallback, useEffect, useState } from "react";
import { Sparkles, Plus, Trash2, Loader2, Package, GripVertical, ArrowUp, ArrowDown, Check, X, Image as ImageIcon, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  listOnboardingApps, createOnboardingApp, updateOnboardingApp,
  reorderOnboardingApps, deleteOnboardingApp,
} from "@/lib/api";
import { fmtBRL } from "@/lib/format";

/**
 * Admin — CRUD 100% editável dos apps do formulário de onboarding.
 * Sem apps padrão fixos — o admin controla toda a lista disponível por plano:
 * adicionar/editar (label + preço adicional) / reordenar (drag ou setas) / remover.
 * Reflete instantaneamente no `/onboard` público.
 */
const PLANOS = [
  { slug: "flua_standard", nome: "FLUA STANDARD" },
  { slug: "flua_advanced", nome: "FLUA ADVANCED" },
  { slug: "flua_top", nome: "FLUA TOP" },
  { slug: "flua_premium", nome: "FLUA PREMIUM" },
];

const parseNum = (v) => {
  const n = Number(String(v || "").replace(",", "."));
  return isFinite(n) ? Math.max(0, n) : 0;
};

// Converte arquivo de imagem em dataURL base64. Rejeita se > 300KB pra não
// estourar payload no Mongo. Aceita PNG/JPG/WebP/SVG/GIF.
const MAX_ICON_BYTES = 300 * 1024;
const ALLOWED_ICON_MIME = ["image/png", "image/jpeg", "image/webp", "image/svg+xml", "image/gif"];
const fileToDataURL = (file) => new Promise((resolve, reject) => {
  if (!file) { reject(new Error("Nenhum arquivo")); return; }
  if (!ALLOWED_ICON_MIME.includes(file.type)) {
    reject(new Error("Formato inválido — use PNG, JPG, WebP, SVG ou GIF")); return;
  }
  if (file.size > MAX_ICON_BYTES) {
    reject(new Error(`Arquivo muito grande (${Math.round(file.size/1024)}KB) — máx 300KB`)); return;
  }
  const reader = new FileReader();
  reader.onerror = () => reject(new Error("Falha ao ler o arquivo"));
  reader.onload = () => resolve(String(reader.result || ""));
  reader.readAsDataURL(file);
});

export default function OnboardingAppsCard() {
  const [data, setData] = useState({ custom: [] });
  const [loading, setLoading] = useState(true);
  const [planoSlug, setPlanoSlug] = useState("flua_standard");
  const [label, setLabel] = useState("");
  const [preco, setPreco] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState("");
  const [editPreco, setEditPreco] = useState("");
  const [dragId, setDragId] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);

  const handleIconUpload = async (app, file) => {
    if (!file) return;
    setUploadingId(app.id);
    try {
      const dataURL = await fileToDataURL(file);
      await updateOnboardingApp(app.id, { icone_url: dataURL });
      toast.success(`Logo do ${app.label} atualizada`);
      await load();
    } catch (err) {
      toast.error(err?.message || err?.response?.data?.detail || "Falha ao subir logo");
    } finally { setUploadingId(null); }
  };

  const removeIcon = async (app) => {
    if (!window.confirm(`Remover a logo do ${app.label}?`)) return;
    try {
      await updateOnboardingApp(app.id, { icone_url: "" });
      toast.success("Logo removida");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao remover logo");
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listOnboardingApps();
      setData({ custom: r?.custom || [] });
    } catch (e) {
      console.error(e);
      toast.error("Falha ao carregar apps");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addApp = async () => {
    if (!label.trim()) { toast.error("Informe o nome do app"); return; }
    setSaving(true);
    try {
      await createOnboardingApp({
        plano_slug: planoSlug,
        label: label.trim(),
        preco_adicional: parseNum(preco),
      });
      toast.success(`"${label.trim()}" adicionado ao ${PLANOS.find(p=>p.slug===planoSlug)?.nome}`);
      setLabel(""); setPreco("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao adicionar app");
    } finally { setSaving(false); }
  };

  const startEdit = (app) => {
    setEditingId(app.id);
    setEditLabel(app.label);
    setEditPreco(app.preco_adicional > 0 ? String(app.preco_adicional).replace(".", ",") : "");
  };

  const cancelEdit = () => { setEditingId(null); setEditLabel(""); setEditPreco(""); };

  const saveEdit = async (app) => {
    if (!editLabel.trim()) { toast.error("Nome não pode ficar vazio"); return; }
    try {
      await updateOnboardingApp(app.id, {
        label: editLabel.trim(),
        preco_adicional: parseNum(editPreco),
      });
      toast.success("App atualizado");
      cancelEdit();
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao salvar");
    }
  };

  const removeApp = async (app) => {
    if (!window.confirm(`Remover "${app.label}" do formulário?`)) return;
    try {
      await deleteOnboardingApp(app.id);
      toast.success("App removido");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao remover");
    }
  };

  // Reordena via UP/DOWN (mobile-safe) e via drag-and-drop nativo.
  const moveApp = async (planoAppsCustom, fromIdx, toIdx) => {
    if (toIdx < 0 || toIdx >= planoAppsCustom.length || fromIdx === toIdx) return;
    const arr = [...planoAppsCustom];
    const [moved] = arr.splice(fromIdx, 1);
    arr.splice(toIdx, 0, moved);
    // Optimistic update
    const otherCustom = data.custom.filter((a) => a.plano_slug !== moved.plano_slug);
    setData((prev) => ({ ...prev, custom: [...otherCustom, ...arr] }));
    try {
      await reorderOnboardingApps(arr.map((a) => a.id));
    } catch {
      toast.error("Falha ao reordenar — recarregando");
      await load();
    }
  };

  const onDragStart = (id) => setDragId(id);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (customApps, targetIdx) => {
    if (!dragId) return;
    const fromIdx = customApps.findIndex((a) => a.id === dragId);
    if (fromIdx >= 0) moveApp(customApps, fromIdx, targetIdx);
    setDragId(null);
  };

  const grouped = PLANOS.map((p) => ({
    ...p,
    customs: data.custom.filter((a) => a.plano_slug === p.slug),
  }));

  return (
    <div className="rounded-xl border border-border bg-card p-5 space-y-4 card-flua" data-testid="onboarding-apps-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/15 grid place-items-center shrink-0">
            <Sparkles className="w-5 h-5 text-primary" strokeWidth={1.5} />
          </div>
          <div>
            <h3 className="font-heading font-semibold text-lg">Apps disponíveis por plano</h3>
            <p className="text-xs text-muted-foreground mt-0.5 max-w-2xl">
              Você tem controle total — adicione, edite, reordene ou remova qualquer app. Sem lista padrão fixa. Reflete na hora no <code className="text-[10px] font-mono">/onboard</code>.
            </p>
          </div>
        </div>
      </div>

      {/* Formulário de cadastro */}
      <div className="rounded-lg border border-primary/30 bg-primary/5 p-4">
        <Label className="text-xs uppercase tracking-widest text-primary font-semibold mb-3 block">Adicionar novo app</Label>
        <div className="grid grid-cols-1 sm:grid-cols-[200px_1fr_150px_auto] gap-2">
          <Select value={planoSlug} onValueChange={setPlanoSlug}>
            <SelectTrigger data-testid="apps-plano-select">
              <SelectValue placeholder="Plano" />
            </SelectTrigger>
            <SelectContent>
              {PLANOS.map((p) => (
                <SelectItem key={p.slug} value={p.slug}>{p.nome}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Ex: Spotify Premium, Duolingo Plus…"
            data-testid="apps-label-input"
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addApp(); } }}
          />
          <Input
            value={preco}
            onChange={(e) => setPreco(e.target.value)}
            placeholder="R$ 0,00 (opcional)"
            inputMode="decimal"
            data-testid="apps-preco-input"
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addApp(); } }}
          />
          <Button onClick={addApp} disabled={saving || !label.trim()} data-testid="apps-add-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" strokeWidth={1.5} /> : <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />}
            Adicionar
          </Button>
        </div>
        <p className="text-[10px] text-muted-foreground mt-2">Deixe o preço vazio pra bônus grátis. Se colocar valor, o cliente paga esse adicional em cima do plano.</p>
      </div>

      {/* Lista por plano */}
      {loading ? (
        <div className="text-center py-6 text-muted-foreground flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> Carregando…
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {grouped.map((p) => (
            <div key={p.slug} className="rounded-lg border border-border bg-background/60 p-3 space-y-2" data-testid={`apps-plano-${p.slug}`}>
              <div className="flex items-center gap-2 pb-2 border-b border-border">
                <Package className="w-3.5 h-3.5 text-primary" strokeWidth={1.5} />
                <span className="font-heading font-semibold text-sm">{p.nome}</span>
                <Badge variant="outline" className="text-[10px] ml-auto">
                  {p.customs.length} {p.customs.length === 1 ? "app" : "apps"}
                </Badge>
              </div>
              <div className="space-y-1">
                {/* Todos os apps são editáveis / reordenáveis / removíveis */}
                {p.customs.map((a, idx) => {
                  const isEditing = editingId === a.id;
                  return (
                    <div
                      key={a.id}
                      draggable={!isEditing}
                      onDragStart={() => onDragStart(a.id)}
                      onDragOver={onDragOver}
                      onDrop={() => onDrop(p.customs, idx)}
                      className={`flex items-center gap-2 text-sm py-1.5 px-1 rounded transition-colors ${
                        dragId === a.id ? "opacity-40" : "hover:bg-accent/50"
                      }`}
                      data-testid={`apps-row-${a.id}`}
                    >
                      <button
                        type="button"
                        className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground shrink-0"
                        title="Arraste para reordenar"
                        aria-label="Arraste para reordenar"
                      >
                        <GripVertical className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </button>
                      {isEditing ? (
                        <>
                          <Input
                            value={editLabel}
                            onChange={(e) => setEditLabel(e.target.value)}
                            className="h-7 text-sm flex-1"
                            data-testid={`apps-edit-label-${a.id}`}
                          />
                          <Input
                            value={editPreco}
                            onChange={(e) => setEditPreco(e.target.value)}
                            placeholder="R$"
                            inputMode="decimal"
                            className="h-7 text-sm w-24"
                            data-testid={`apps-edit-preco-${a.id}`}
                          />
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-emerald-500" onClick={() => saveEdit(a)} data-testid={`apps-save-${a.id}`}>
                            <Check className="w-3.5 h-3.5" strokeWidth={1.5} />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground" onClick={cancelEdit}>
                            <X className="w-3.5 h-3.5" strokeWidth={1.5} />
                          </Button>
                        </>
                      ) : (
                        <>
                          {/* Logo do app (miniatura + upload) */}
                          <label
                            htmlFor={`app-icon-${a.id}`}
                            className="relative w-7 h-7 rounded bg-background border border-border shrink-0 grid place-items-center cursor-pointer hover:border-primary transition-colors overflow-hidden group/icon"
                            title={a.icone_url ? "Trocar logo" : "Subir logo (PNG/JPG/WebP até 300KB)"}
                          >
                            {uploadingId === a.id ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" strokeWidth={1.5} />
                            ) : a.icone_url ? (
                              <>
                                <img
                                  src={a.icone_url}
                                  alt={a.label}
                                  className="w-full h-full object-contain"
                                  loading="lazy"
                                />
                                <span className="absolute inset-0 bg-black/60 opacity-0 group-hover/icon:opacity-100 transition-opacity grid place-items-center">
                                  <Upload className="w-3 h-3 text-white" strokeWidth={2} />
                                </span>
                              </>
                            ) : (
                              <ImageIcon className="w-3.5 h-3.5 text-muted-foreground" strokeWidth={1.5} />
                            )}
                            <input
                              id={`app-icon-${a.id}`}
                              type="file"
                              accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
                              className="hidden"
                              data-testid={`apps-icon-input-${a.id}`}
                              onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) handleIconUpload(a, f);
                                e.target.value = "";
                              }}
                            />
                          </label>
                          <span className="truncate flex-1" onClick={() => startEdit(a)} title="Clique pra editar">
                            {a.label}
                          </span>
                          {Number(a.preco_adicional) > 0 && (
                            <Badge className="bg-emerald-500/15 text-emerald-500 border-emerald-500/40 text-[10px] tabular-nums shrink-0">
                              +{fmtBRL(a.preco_adicional)}
                            </Badge>
                          )}
                          <div className="flex items-center gap-0.5 shrink-0">
                            {a.icone_url && (
                              <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground hover:text-red-500 hover:bg-red-500/10"
                                onClick={() => removeIcon(a)} title="Remover logo" data-testid={`apps-icon-remove-${a.id}`}>
                                <X className="w-3 h-3" strokeWidth={1.5} />
                              </Button>
                            )}
                            <Button size="icon" variant="ghost" className="h-6 w-6" disabled={idx === 0}
                              onClick={() => moveApp(p.customs, idx, idx - 1)} title="Mover pra cima" data-testid={`apps-up-${a.id}`}>
                              <ArrowUp className="w-3 h-3" strokeWidth={1.5} />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-6 w-6" disabled={idx === p.customs.length - 1}
                              onClick={() => moveApp(p.customs, idx, idx + 1)} title="Mover pra baixo" data-testid={`apps-down-${a.id}`}>
                              <ArrowDown className="w-3 h-3" strokeWidth={1.5} />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-6 w-6 text-red-500 hover:bg-red-500/10"
                              onClick={() => removeApp(a)} title="Remover" data-testid={`apps-delete-${a.id}`}>
                              <Trash2 className="w-3 h-3" strokeWidth={1.5} />
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
                {p.customs.length === 0 && (
                  <div className="text-xs text-muted-foreground italic py-2">Nenhum app cadastrado. Adicione o primeiro acima.</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
